import * as Y from 'yjs';
import type { MindMapNode } from '@/model/types';
import type { MindMapDocument } from '@/model/document';
import type { SerializedNode } from './types';

/**
 * Recursively serialize a node and its children into a plain object
 * suitable for clipboard storage.
 */
export function serializeSubtree(
  nodeId: string,
  nodes: Map<string, MindMapNode>,
): SerializedNode | undefined {
  const node = nodes.get(nodeId);
  if (!node) return undefined;

  const children: SerializedNode[] = [];
  for (const childId of node.childIds) {
    const child = serializeSubtree(childId, nodes);
    if (child) {
      children.push(child);
    }
  }

  const serialized: SerializedNode = {
    text: node.text,
    notes: node.notes,
    children,
  };

  if (node.style) {
    serialized.style = { ...node.style };
  }

  if (node.links && node.links.length > 0) {
    serialized.links = node.links.map((l) => ({
      url: l.url,
      ...(l.label !== undefined ? { label: l.label } : {}),
    }));
  }

  return serialized;
}

/**
 * Recursively create nodes from serialized data, generating new IDs.
 * Returns the root node ID of the pasted subtree.
 */
export function deserializeSubtree(
  serialized: SerializedNode,
  mapDoc: MindMapDocument,
  parentId: string,
): string {
  // Create the node as a child of the parent (appended at end)
  const newId = mapDoc.addChild(parentId, serialized.text);

  // Set notes if present
  if (serialized.notes) {
    mapDoc.updateNodeNotes(newId, serialized.notes);
  }

  // Set style if present
  if (serialized.style) {
    setNodeStyle(mapDoc, newId, serialized.style);
  }

  // Add links if present
  if (serialized.links) {
    for (const link of serialized.links) {
      mapDoc.addNodeLink(newId, link.url, link.label);
    }
  }

  // Recursively create children
  for (const child of serialized.children) {
    deserializeSubtree(child, mapDoc, newId);
  }

  return newId;
}

/**
 * Recursively create nodes from serialized data as a sibling, inserting
 * at a specific index in the parent's childIds.
 * Returns the root node ID of the deserialized subtree.
 */
export function deserializeSubtreeAtIndex(
  serialized: SerializedNode,
  mapDoc: MindMapDocument,
  parentId: string,
  insertIndex: number,
): string {
  // Create the node at a specific position in the parent's children
  const newId = mapDoc.addChildAtIndex(parentId, serialized.text, insertIndex);

  // Set notes if present
  if (serialized.notes) {
    mapDoc.updateNodeNotes(newId, serialized.notes);
  }

  // Set style if present
  if (serialized.style) {
    setNodeStyle(mapDoc, newId, serialized.style);
  }

  // Add links if present
  if (serialized.links) {
    for (const link of serialized.links) {
      mapDoc.addNodeLink(newId, link.url, link.label);
    }
  }

  // Recursively create children (these append normally)
  for (const child of serialized.children) {
    deserializeSubtree(child, mapDoc, newId);
  }

  return newId;
}

/**
 * Helper to set style overrides on a node via the Yjs document layer.
 */
function setNodeStyle(
  mapDoc: MindMapDocument,
  nodeId: string,
  style: NonNullable<MindMapNode['style']>,
): void {
  const yNodes = mapDoc.doc.getMap('nodes') as Y.Map<Y.Map<unknown>>;
  const yNode = yNodes.get(nodeId);
  if (!yNode) return;

  mapDoc.doc.transact(() => {
    let yStyle = yNode.get('style') as Y.Map<unknown> | undefined;
    if (!yStyle) {
      yStyle = new Y.Map<unknown>();
      yNode.set('style', yStyle);
    }
    if (style.backgroundColor !== undefined) yStyle.set('backgroundColor', style.backgroundColor);
    if (style.textColor !== undefined) yStyle.set('textColor', style.textColor);
    if (style.fontSize !== undefined) yStyle.set('fontSize', style.fontSize);
    if (style.fontWeight !== undefined) yStyle.set('fontWeight', style.fontWeight);
    if (style.borderColor !== undefined) yStyle.set('borderColor', style.borderColor);
  });
}
